import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { CalendarCheck, Mail, MapPin, Phone, WifiOff } from 'lucide-react';
import { LOCALE_LABELS, useI18n } from '../lib/i18n';
import { cn } from './ui';

/**
 * The page frame: the pharmacy's name on RxManager's brown band, the step on a white panel
 * lifted over it, and "Appointments by OneRx" underneath.
 *
 * Landmarks matter more than they look: a screen-reader user navigates by
 * `header`/`main`/`footer` rather than by scrolling, and the skip link is what
 * lets a keyboard user reach the form without tabbing through the header first.
 */

/** What a page learns about its pharmacy after it loads (an offer, a place on the waitlist). */
export interface ShellBrand {
  name?: string | null;
  logoUrl?: string | null;
  phone?: string | null;
}

const BrandContext = createContext<(brand: ShellBrand) => void>(() => undefined);

/**
 * Names the pharmacy in the band once a page knows it. The manage, waitlist and offer pages only
 * learn which pharmacy they belong to when their link resolves.
 */
export function useShellBrand(brand: ShellBrand | null | undefined): void {
  const set = useContext(BrandContext);
  const name = brand?.name ?? null;
  const logoUrl = brand?.logoUrl ?? null;
  const phone = brand?.phone ?? null;
  useEffect(() => {
    if (name) set({ name, logoUrl, phone });
  }, [set, name, logoUrl, phone]);
}

const mapsUrl = (address: string): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

export function Shell({
  title,
  blurb,
  logoUrl,
  supportEmail,
  supportPhone,
  address,
  bookingPage = false,
  children,
}: {
  title?: string;
  blurb?: string | null;
  logoUrl?: string | null;
  supportEmail?: string | null;
  supportPhone?: string | null;
  /** The address, for a pharmacy with one branch. */
  address?: string | null;
  /** The pharmacy's booking page: "Book an appointment" under its name when it has no blurb. */
  bookingPage?: boolean;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const [learned, setLearned] = useState<ShellBrand | null>(null);
  const name = title ?? learned?.name ?? null;
  const logo = logoUrl ?? learned?.logoUrl ?? null;
  const phone = supportPhone ?? learned?.phone ?? null;
  const contacts = Boolean(address || phone || supportEmail);
  const tagline = blurb || (bookingPage ? t('app.title') : null);

  return (
    <BrandContext.Provider value={setLearned}>
      <div className="flex min-h-full flex-col">
        <a
          href="#main"
          className="sr-only rounded-lg bg-card px-4 py-2 font-semibold text-primary-strong shadow-card focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50"
        >
          {t('app.skipToContent')}
        </a>

        <header className="relative overflow-hidden bg-hero text-hero-foreground">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-hero via-hero to-hero-deep"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-20 -top-28 h-72 w-72 rounded-full bg-white/[0.06]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-32 left-1/4 h-64 w-64 rounded-full bg-black/10"
          />
          <div className="relative mx-auto max-w-3xl px-4 pb-24 pt-6 sm:px-6 sm:pb-28 sm:pt-8">
            {/* On a phone the mark and the languages share the top row and the name has a row of its
                own, so it is never cut off; from `sm` up all three sit on one row. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
              <BrandMark name={name} logoUrl={logo} />
              <div className="order-last w-full min-w-0 sm:order-none sm:w-auto sm:flex-1">
                <p className="text-xl font-semibold leading-tight">{name ?? t('app.title')}</p>
                {tagline && <p className="mt-1 line-clamp-2 text-sm text-hero-muted">{tagline}</p>}
              </div>
              <div className="ml-auto">
                <LanguageSwitch tone="hero" />
              </div>
            </div>
            {contacts && (
              <ul className="mt-4 flex flex-wrap gap-2 text-sm">
                {address && (
                  <li>
                    <a href={mapsUrl(address)} target="_blank" rel="noreferrer noopener" className={BAND_CHIP}>
                      <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span className="truncate">{address}</span>
                    </a>
                  </li>
                )}
                {phone && (
                  <li>
                    <a href={`tel:${phone}`} className={BAND_CHIP}>
                      <Phone className="h-4 w-4 shrink-0" aria-hidden="true" />
                      {phone}
                    </a>
                  </li>
                )}
                {supportEmail && (
                  <li>
                    <a href={`mailto:${supportEmail}`} className={BAND_CHIP}>
                      <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span className="truncate">{supportEmail}</span>
                    </a>
                  </li>
                )}
              </ul>
            )}
          </div>
        </header>

        <main id="main" className="relative mx-auto -mt-16 w-full max-w-3xl flex-1 px-3 sm:-mt-20 sm:px-6">
          {/* `--panel-pad` is the panel's padding, which the phone's bottom bar reaches past. */}
          <div className="rounded-2xl border border-black/5 bg-card p-5 shadow-panel [--panel-pad:1.25rem] sm:p-8 sm:[--panel-pad:2rem]">
            <OfflineNotice />
            {children}
          </div>
        </main>

        <footer className="mx-auto w-full max-w-3xl px-4 py-8 text-center text-sm text-muted-foreground sm:px-6">
          <p className="inline-flex items-center gap-2">
            <RxGlyph />
            {t('app.poweredBy')}
          </p>
        </footer>
      </div>
    </BrandContext.Provider>
  );
}

/** A contact on the band: a quiet pill, cream on a lighter brown. */
const BAND_CHIP = cn(
  'inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-hero-foreground ring-1 ring-white/15',
  'transition-colors hover:bg-white/15',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hero-foreground',
);

/**
 * The pharmacy's logo on a white tile (logos are drawn for white), or its initials. Decorative:
 * the name is written beside it.
 */
function BrandMark({ name, logoUrl }: { name: string | null; logoUrl: string | null }) {
  if (logoUrl) {
    return (
      <span aria-hidden="true" className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-white p-1 shadow-sm">
        <img src={logoUrl} alt="" className="max-h-full max-w-full object-contain" />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-hero-foreground text-lg font-bold tracking-tight text-hero shadow-sm"
    >
      {name ? initials(name) : <CalendarCheck className="h-6 w-6" />}
    </span>
  );
}

/** "Lakeview Family Pharmacy" → "LF". Words like "the" and "pharmacy" say nothing. */
export function initials(name: string): string {
  const quiet = new Set(['the', 'la', 'le', 'les', 'de', 'des', 'du', 'and', 'et', '&', 'pharmacy', 'pharmacie']);
  const words = name.split(/\s+/).filter((word) => word && !quiet.has(word.toLowerCase()));
  const picked = (words.length > 0 ? words : name.split(/\s+/)).slice(0, 2);
  return picked.map((word) => Array.from(word)[0] ?? '').join('').toUpperCase();
}

/** OneRx's mark, small, beside "Appointments by OneRx". */
function RxGlyph() {
  return (
    <span
      aria-hidden="true"
      className="grid h-6 w-6 place-items-center rounded-md bg-hero text-[0.625rem] font-bold tracking-tight text-[#b0dbcf]"
    >
      Rx
    </span>
  );
}

/** The browser's own offline flag, said plainly before a patient fills in a form that can't send. */
export function OfflineNotice() {
  const { t } = useI18n();
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const on = (): void => setOnline(true);
    const off = (): void => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  if (online) return null;
  return (
    <p
      role="status"
      className="mb-6 flex items-start gap-3 rounded-xl border border-warning/50 bg-warning/15 p-4 text-base text-foreground"
    >
      <WifiOff className="mt-0.5 h-5 w-5 shrink-0 text-warning-foreground" aria-hidden="true" />
      {t('app.offline')}
    </p>
  );
}

/**
 * Hidden entirely when the pharmacy offers one language — a switcher with a
 * single option is noise in the tab order.
 *
 * On the brown band it is cream; inside the pharmacy website's frame, where there is no band, it
 * takes the page's emerald.
 */
export function LanguageSwitch({ tone = 'light' }: { tone?: 'hero' | 'light' }) {
  const { locale, setLocale, available, t } = useI18n();
  if (available.length < 2) return null;
  const onHero = tone === 'hero';

  return (
    <div
      role="group"
      aria-label={t('app.language')}
      className={cn(
        'flex shrink-0 gap-0.5 rounded-full p-1',
        onHero ? 'bg-black/20 ring-1 ring-white/15' : 'bg-muted',
      )}
    >
      {available.map((option) => (
        <button
          key={option}
          type="button"
          lang={option}
          aria-current={option === locale ? 'true' : undefined}
          onClick={() => setLocale(option)}
          className={cn(
            'min-h-[2.25rem] rounded-full px-3 text-sm font-medium transition-colors',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
            onHero ? 'focus-visible:outline-hero-foreground' : 'focus-visible:outline-ring',
            option === locale
              ? onHero
                ? 'bg-hero-foreground text-hero shadow-sm'
                : 'bg-primary text-primary-foreground shadow-sm'
              : onHero
                ? 'text-hero-foreground hover:bg-white/10'
                : 'text-muted-foreground hover:bg-card',
          )}
        >
          {LOCALE_LABELS[option]}
        </button>
      ))}
    </div>
  );
}

/**
 * The frame while the pharmacy's page loads. There is no language yet (the pharmacy says which
 * it offers), so it has no words of its own beyond the spinner's.
 */
export function LoadingFrame({ label }: { label: string }) {
  return (
    <div className="min-h-full">
      <div className="h-44 bg-gradient-to-br from-hero via-hero to-hero-deep sm:h-48" />
      <div className="mx-auto -mt-16 max-w-3xl px-3 sm:-mt-20 sm:px-6">
        <div className="rounded-2xl border border-black/5 bg-card p-5 shadow-panel sm:p-8">
          <p className="flex items-center gap-2.5 py-6 text-base text-muted-foreground">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
            <span role="status">{label}</span>
          </p>
        </div>
      </div>
    </div>
  );
}
